/**
 * Records query options factories
 *
 * Centralizes query configuration for record-related queries.
 * Used in route loaders for prefetching and in components via useQuery.
 *
 * Migration guide:
 * ================
 * Current (useRecordsData hook with useState/useEffect):
 *   const { records, loading, error } = useRecordsData();
 *
 * Target (useQuery with query options factory):
 *   const { data, isLoading, error } = useQuery(
 *     recordsListOptions(apiClient, selectedOrgId, { page: 1, limit: 100 })
 *   );
 *
 * With loader prefetch:
 *   export const Route = createFileRoute('/records')({
 *     loader: ({ context: { queryClient } }) => {
 *       const apiClient = getRegisteredApiClient();
 *       if (!apiClient) return;
 *       return queryClient.ensureQueryData(
 *         recordsListOptions(apiClient, selectedOrgId, { page: 1, limit: 100 })
 *       );
 *     },
 *   });
 */

import { queryOptions } from '@tanstack/react-query';
import type { ApiClient } from '@/lib/api';
import { toTranscriptDataFromApi } from '@/lib/api/mappers';
import { retryTransientOnly } from '@/lib/query-retry';

interface RecordsListParams {
  page?: number;
  limit?: number;
  projectId?: string;
  /** The list's multi-project filter; wins over `projectId` when non-empty. */
  projectIds?: readonly string[];
  /** The list's demo policy; absent — the server's default (demo included). */
  includeDemo?: 'yes' | 'no';
  status?: string;
  type?: 'call' | 'chat' | 'email';
  crmEntityType?: 'deal' | 'lead' | 'contact' | 'company';
  callCategory?: string;
  callDirection?: 'inbound' | 'outbound' | 'unknown';
  agentId?: string;
  seriesId?: string;
  excludeMissed?: boolean;
  search?: string;
  /** Period shorthand: 'today' | 'week' | 'month' | 'quarter'. Converted to createdAfter/createdBefore. */
  period?: string;
  /** Direct ISO 8601 date filters (override period if both set) */
  createdAfter?: string;
  createdBefore?: string;
}

/**
 * Convert a period shorthand to ISO 8601 date range strings.
 * Returns { createdAfter, createdBefore } to pass directly to the API.
 */
function periodToDateRange(period: string): {
  createdAfter: string;
  createdBefore: string;
} {
  const now = new Date();

  if (period === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    return { createdAfter: start.toISOString(), createdBefore: end.toISOString() };
  }

  if (period === 'week') {
    const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { createdAfter: start.toISOString(), createdBefore: now.toISOString() };
  }

  if (period === 'month') {
    const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { createdAfter: start.toISOString(), createdBefore: now.toISOString() };
  }

  if (period === 'quarter') {
    const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    return { createdAfter: start.toISOString(), createdBefore: now.toISOString() };
  }

  // Fallback: no filter
  return { createdAfter: '', createdBefore: '' };
}

export function recordsListOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  params: RecordsListParams = {}
) {
  const {
    page = 1,
    limit = 100,
    projectId,
    projectIds,
    includeDemo,
    status,
    type,
    crmEntityType,
    callCategory,
    callDirection,
    agentId,
    seriesId,
    excludeMissed,
    search,
    period,
    createdAfter: directCreatedAfter,
    createdBefore: directCreatedBefore,
  } = params;
  const offset = (page - 1) * limit;

  // Direct date params take precedence over period shorthand
  const dateRange = period ? periodToDateRange(period) : null;
  const createdAfter = directCreatedAfter || dateRange?.createdAfter || undefined;
  const createdBefore = directCreatedBefore || dateRange?.createdBefore || undefined;

  return queryOptions({
    queryKey: [
      'records',
      'list',
      selectedOrgId,
      {
        page,
        limit,
        projectId,
        projectIds,
        includeDemo,
        status,
        type,
        crmEntityType,
        callCategory,
        callDirection,
        agentId,
        seriesId,
        excludeMissed,
        search,
        period,
        createdAfter,
        createdBefore,
      },
    ] as const,
    queryFn: () =>
      apiClient.records.getRecords({
        limit: String(limit),
        offset: String(offset),
        ...(projectIds && projectIds.length > 0
          ? { projectIds: projectIds.join(',') }
          : projectId
            ? { projectId }
            : {}),
        ...(includeDemo ? { includeDemo } : {}),
        ...(status ? { status } : {}),
        ...(type ? { type } : {}),
        ...(crmEntityType ? { crmEntityType } : {}),
        ...(callCategory ? { callCategory } : {}),
        ...(callDirection ? { callDirection } : {}),
        ...(agentId ? { agentId } : {}),
        ...(seriesId ? { seriesId } : {}),
        ...(excludeMissed ? { excludeMissed: 'true' } : {}),
        ...(search ? { search } : {}),
        ...(createdAfter ? { createdAfter } : {}),
        ...(createdBefore ? { createdBefore } : {}),
      }),
    enabled: !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function recordDetailOptions(
  apiClient: ApiClient,
  recordId: string,
  selectedOrgId: string | null,
  options: { enabled?: boolean } = {}
) {
  return queryOptions({
    queryKey: ['records', 'detail', recordId, selectedOrgId] as const,
    queryFn: () => apiClient.records.getRecordById(recordId),
    // Defaults to true: the hover/neighbor prefetch call sites (RecordsPage)
    // pass no third argument and always want an eager fetch. useRecordDetail
    // passes its own composed condition, including the org-match gate that
    // keeps a switched-org request for the previous org's record from ever
    // being sent — see expectedOrgId there.
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * A reader that does not know the record's analysis (the Objections excerpt):
 * not `null`, which would read the entry a panel filled before the analysis.
 */
export const TRANSCRIPT_ANALYSIS_UNKNOWN = 'unknown';

/**
 * Record transcript as the API returns it — segments keep their `role` and a
 * missing speaker stays missing. The record panel maps it
 * (`recordTranscriptOptions`), the Objections screen cuts best-answer excerpts
 * from it (#846).
 * Shared by useRecordDetail and the hover/neighbor prefetch in RecordsPage —
 * the queryKey must stay identical for the prefetch to warm the same entry.
 * org id in the key so an org switch invalidates the cached transcript for
 * the same recordId in the other org's tenant.
 *
 * `analysisId` — the record's current analysis as the reader knows it: `null`
 * when the record has none yet, `TRANSCRIPT_ANALYSIS_UNKNOWN` when the reader
 * cannot know it. The server answers with the current analysis's transcript,
 * else the newest version, so a call's answer moves from the ASR source
 * labelled `speaker_N` to the cleaned version labelled with names. The cleaned
 * version is written just BEFORE the analysis, and the key follows only the
 * analysis: it tells apart the transcript fetched before the analysis from the
 * one beside it, not every version. Under one key per record, a panel opened
 * mid-pipeline (or a row prefetched on hover) kept the source for the cache's
 * lifetime, and the analysis's role map turned its labels into "Agent/Client"
 * where the names were known (#1851).
 */
export function recordTranscriptRawOptions(
  apiClient: ApiClient,
  recordId: string,
  selectedOrgId: string | null,
  analysisId: string | null | typeof TRANSCRIPT_ANALYSIS_UNKNOWN
) {
  return queryOptions({
    queryKey: ['records', 'transcript', recordId, selectedOrgId, analysisId] as const,
    queryFn: () => apiClient.records.getRecordTranscript(recordId),
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
  });
}

/** Record transcript mapped to UI TranscriptData, over the shared raw entry. */
export function recordTranscriptOptions(
  apiClient: ApiClient,
  recordId: string,
  selectedOrgId: string | null,
  analysisId: string | null
) {
  return queryOptions({
    ...recordTranscriptRawOptions(apiClient, recordId, selectedOrgId, analysisId),
    select: toTranscriptDataFromApi,
  });
}
